'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type StepSnapshot<T = any> = {
  data: T;
  ts: number;
};

type WizardState = {
  version: number;
  lastStep: number;
  steps: Record<string, StepSnapshot>;
  inFlight?: boolean;
  wizardRunId: string;
  updatedAt: number;
  expiresAt: number;
};

type StorageKind = 'local' | 'session';

type Options = {
  wizardId?: string | null;
  version?: number;
  ttlDays?: number; // can be fractional for tests
  storage?: StorageKind;
};

const genId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? // @ts-ignore
      crypto.randomUUID()
    : `wiz_${Math.random().toString(36).slice(2)}_${Date.now()}`;

function now() {
  return Date.now();
}

function makeKey(wizardId: string, version: number) {
  return `onboard:${wizardId}:v${version}`;
}

function safeParse(json: string | null): WizardState | null {
  if (!json) return null;
  try {
    return JSON.parse(json) as WizardState;
  } catch (_e) {
    return null;
  }
}

export function useWizardBrowserStorage({
  wizardId,
  version = 1,
  ttlDays = 14,
  storage = 'local',
}: Options) {
  const effectiveId = wizardId ?? 'unknown';
  const key = useMemo(
    () => makeKey(effectiveId, version),
    [effectiveId, version],
  );
  const ttlMs = useMemo(
    () => Math.max(0, ttlDays) * 24 * 60 * 60 * 1000,
    [ttlDays],
  );

  const store: Storage | null = useMemo(() => {
    if (typeof window === 'undefined') return null;
    return storage === 'session' ? window.sessionStorage : window.localStorage;
  }, [storage]);

  const readState = useCallback((): WizardState | null => {
    if (!store) return null;
    const raw = store.getItem(key);
    const parsed = safeParse(raw);
    if (!parsed) return null;
    if (parsed.expiresAt && parsed.expiresAt < now()) {
      try {
        store.removeItem(key);
      } catch {}
      return null;
    }
    return parsed;
  }, [key, store]);

  const [state, setState] = useState<WizardState | null>(() => readState());
  const isMounted = useRef(false);

  // cross-tab sync (only for localStorage; sessionStorage does not fire 'storage' across tabs reliably)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = (e: StorageEvent) => {
      if (e.key !== key) return;
      setState(readState());
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [key, readState]);

  const persist = useCallback(
    (next: WizardState) => {
      if (!store) return;
      try {
        store.setItem(key, JSON.stringify(next));
      } catch {
        // ignore quota or serialization errors
      }
    },
    [key, store],
  );

  // Ensure wizardRunId exists on first mount with state
  useEffect(() => {
    if (isMounted.current) return;
    isMounted.current = true;
    if (!state) return;
    if (!state.wizardRunId) {
      const next = { ...state, wizardRunId: genId() } as WizardState;
      persist(next);
      setState(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const touch = useCallback(
    (mutator: (prev: WizardState) => WizardState) => {
      const base: WizardState = state ?? {
        version,
        lastStep: 0,
        steps: {},
        inFlight: false,
        wizardRunId: genId(),
        updatedAt: now(),
        expiresAt: now() + ttlMs,
      };
      const next = mutator(base);
      next.updatedAt = now();
      next.expiresAt = now() + ttlMs;
      setState(next);
      persist(next);
    },
    [persist, state, ttlMs, version],
  );

  const getStepData = useCallback(
    <T = any>(stepId: string, fallback?: T): T | undefined => {
      const latest = state ?? readState();
      const snap = latest?.steps?.[stepId];
      return (snap?.data as T) ?? fallback;
    },
    [readState, state],
  );

  const saveStep = useCallback(
    <T = any>(stepId: string, data: T) => {
      touch((prev) => ({
        ...prev,
        steps: {
          ...prev.steps,
          [stepId]: { data, ts: now() },
        },
      }));
    },
    [touch],
  );

  const lastStep = state?.lastStep ?? 0;

  const setLastStep = useCallback(
    (step: number) => {
      touch((prev) => ({ ...prev, lastStep: Math.max(0, step) }));
    },
    [touch],
  );

  const clearAll = useCallback(() => {
    if (!store) return;
    try {
      store.removeItem(key);
    } catch {}
    setState(null);
  }, [key, store]);

  const inFlight = !!state?.inFlight;
  const setInFlight = useCallback(
    (flag: boolean) => {
      touch((prev) => ({ ...prev, inFlight: !!flag }));
    },
    [touch],
  );

  return {
    key,
    lastStep,
    setLastStep,
    getStepData,
    saveStep,
    clearAll,
    inFlight,
    setInFlight,
    wizardRunId: state?.wizardRunId,
  } as const;
}
